import React, { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import CourseCard from "../components/CourseCard";
import API from "../utils/axiosInstance";
import "./Courses.css";
import { useTranslation } from "react-i18next";

// 🔒 Feature flag: toggle payments globally from here (or wire to env)
const PAYMENTS_ENABLED = false;

const Courses = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [inProgressCourses, setInProgressCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [scheduleList, setScheduleList] = useState([]);
  const [accessList, setAccessList] = useState([]);

  const { t, i18n } = useTranslation();

  const hasAccess = (courseId) => accessList.includes(courseId);

  const fetchUserSchedules = async (courseId, userId) => {
    try {
      const res = await API.get(`/schedules/user/${userId}`);
      const allUserSchedules = res.data || [];
      const courseSchedules = allUserSchedules.filter(
        (s) => s.schedule?.courseId === courseId
      );
      setScheduleList(courseSchedules);
    } catch (err) {
      console.error("Failed to fetch user-specific schedule", err);
    }
  };

  const handleCourseClick = async (course) => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      alert(t("LoginToAccess"));
      return;
    }

    if (!hasAccess(course.id)) {
      // 🚫 Payments are disabled: don't attempt purchase
      if (!PAYMENTS_ENABLED) {
        // alert(t("PurchasesTemporarilyDisabled") || "Purchases are temporarily disabled.");
        return;
      }

      const confirmPurchase = window.confirm(t("PurchaseToAccessCourse"));
      if (confirmPurchase) {
        initiatePayment(course.id, course.price);
      }
      return;
    }

    setSelectedCourse(course);
    await fetchUserSchedules(course.id, user.id);
  };

  const initiatePayment = async (courseId, price) => {
    // Extra safety—never run while disabled
    if (!PAYMENTS_ENABLED) {
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        alert(t("LoginToPurchase"));
        return;
      }

      const params = {
        gateway: "paypal",
        accountId: 0,
        amount: price,
        currency: "USD",
        note: `Purchase Course ${courseId}`,
        courseId,
      };

      const res = await API.post("/orders/create", {
        params,
        account: {},
        customer: user,
      });

      if (res.data.order?.link) {
        window.location.href = res.data.order.link;
      } else {
        alert(t("FailedToCreatePaymentLink"));
      }
    } catch (err) {
      console.error("❌ Payment error:", err);
      alert(t("PaymentFailed"));
    }
  };

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user"));
        const res = await API.get("/courses");
        const allCourses = res.data || [];
        setCompletedCourses(allCourses.filter((c) => c.status === "completed"));
        setInProgressCourses(allCourses.filter((c) => c.status === "in_progress"));

        if (user?.id) {
          const accessRes = await API.get(`/courses/user-access/${user.id}`);
          setAccessList(accessRes.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch courses or access:", error);
      }
    };

    fetchCourses();
  }, []);

  // Optional: tiny banner to clarify status to users
  // const showPaymentsBanner = !PAYMENTS_ENABLED;

  return (
    <div
      className="dashboard-wrapper"
      dir={["ar", "fa", "ir", "he"].includes(i18n.language) ? "rtl" : "ltr"}
      style={{
        flexDirection: ["ar", "fa", "ir", "he"].includes(i18n.language)
          ? "row-reverse"
          : "row",
      }}
    >
      <div className="sidebar-fixed">
        <Sidebar isOpen={isSidebarOpen} />
      </div>

      <div className="dashboard-main">
        <Navbar onToggleSidebar={() => setIsSidebarOpen((prev) => !prev)} />

        {/* {showPaymentsBanner && (
          <div className="notice-banner">
            {t("PurchasesTemporarilyDisabled") || "Purchases are temporarily disabled."}
          </div>
        )} */}

        <div className="courses-content">
          {completedCourses.length > 0 && (
            <>
              <h4 className="section-title">{t("CompletedCourses")}</h4>
              <div className="courses-grid">
                {completedCourses.map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    hasAccess={hasAccess(course.id)}
                    onClick={() => handleCourseClick(course)}
                  />
                ))}
              </div>
            </>
          )}

          <h4 className="section-title">{t("CoursesInProgress")}</h4>
          <div className="courses-grid">
            {inProgressCourses.length > 0 ? (
              inProgressCourses.map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  hasAccess={hasAccess(course.id)}
                  onClick={() => handleCourseClick(course)}
                />
              ))
            ) : (
              <p>{t("NoInProgressCourses")}</p>
            )}
          </div>
        </div>
      </div>

      {selectedCourse && (
        <div className="modal-overlay" onClick={() => setSelectedCourse(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h4>
              {selectedCourse.title} – {t("Schedule")}
            </h4>

            {scheduleList.length > 0 ? (
              <ul className="schedule-list">
                {scheduleList.map((s) => (
                  <li key={s.id}>
                    <strong>{s.schedule?.title}</strong>
                    <br />
                    📅 {s.schedule?.date} ⏰ {s.schedule?.time}
                    <br />
                    🔗{" "}
                    <a href={s.joinLink} target="_blank" rel="noreferrer">
                      {t("joinClass")}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p>{t("NoSessionsScheduled")}</p>
            )}

            <button className="modal-close-btn" onClick={() => setSelectedCourse(null)}>
              {t("Close")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;
